import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './SubmitDetails.css';

const API_BASE      = process.env.REACT_APP_API_BASE || '';
const CAL_NAMESPACE = 'astrologerkavitha/astrology-session';
const HOLD_MINUTES  = 15;

const SERVICES = [
  { name: 'Whole Life Reading',                 price: '$60'  },
  { name: 'Relationship Compatibility Reading',  price: '$25'  },
  { name: 'Career and Finance Reading',          price: '$25'  },
  { name: 'Birth Time Rectification',            price: '$60'  },
  { name: 'Vaastu Shastra Consultation',         price: '$150' },
];

const SERVICE_DURATIONS: Record<string, number> = {
  'Whole Life Reading':                60,
  'Relationship Compatibility Reading': 30,
  'Career and Finance Reading':         30,
  'Birth Time Rectification':           60,
  'Vaastu Shastra Consultation':        90,
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

const fmtDob = (dob: string) => {
  if (!dob) return '';
  const [y, m, d] = dob.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

const fmtTime = (t: string) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
};

type PageState = 'intake' | 'loading' | 'ready' | 'invalid' | 'expired' | 'cancelled' | 'confirmed' | 'refunded';

const SubmitDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token') || '';

  const [token, setToken]   = useState(urlToken);
  const tokenRef             = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const [pageState, setPageState]             = useState<PageState>(urlToken ? 'loading' : 'intake');
  const [customerName, setCustomerName]       = useState('');
  const [customerEmail, setCustomerEmail]     = useState('');
  const [customerDob, setCustomerDob]         = useState('');
  const [customerTob, setCustomerTob]         = useState('');
  const [customerPob, setCustomerPob]         = useState('');
  const [calBookingUid, setCalBookingUid]               = useState('');
  const [step, setStep]                                 = useState(1);
  const [appointmentTime, setAppointmentTime]           = useState('');
  const [prevAppointmentTime, setPrevAppointmentTime]   = useState('');
  const [serviceSelected, setServiceSelected]           = useState('');
  const [stripeUrl, setStripeUrl]             = useState('');

  // Step 1 — intake form fields
  const [name, setName]                 = useState('');
  const [email, setEmail]               = useState('');
  const [phone, setPhone]               = useState('');
  const [dob, setDob]                   = useState('');
  const [timeOfBirth, setTimeOfBirth]   = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [service, setService]           = useState('');
  const [questions, setQuestions]       = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [resetting, setResetting]   = useState(false);

  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [slotExpired, setSlotExpired]     = useState(false);

  // Details edit modal (Step 3)
  const [showModal, setShowModal]       = useState(false);
  const [modalName, setModalName]       = useState('');
  const [modalDob, setModalDob]         = useState('');
  const [modalTob, setModalTob]         = useState('');
  const [modalPob, setModalPob]         = useState('');
  const [modalService, setModalService] = useState('');
  const [updating, setUpdating]         = useState(false);
  const [modalError, setModalError]     = useState('');

  // ── Cal.com embed init ────────────────────────────────────────────────────
  useEffect(() => {
    const win = window as any;
    if (!win.Cal) {
      win.Cal = function (...args: any[]) {
        const cal = win.Cal;
        if (!cal.loaded) {
          cal.ns = {}; cal.q = cal.q || [];
          const s = document.createElement('script');
          s.src = 'https://app.cal.com/embed/embed.js'; s.async = true;
          document.head.appendChild(s);
          cal.loaded = true;
        }
        if (args[0] === 'init') {
          const api: any = (...a: any[]) => { api.q.push(a); };
          api.q = [];
          const ns = args[1];
          if (typeof ns === 'string') {
            cal.ns[ns] = cal.ns[ns] || api;
            cal.ns[ns].q.push(args);
            cal.q.push(['init', ns, args[2]]);
          } else { cal.q.push(args); }
          return;
        }
        cal.q.push(args);
      };
      win.Cal.q = [];
    }

    win.Cal('init', { origin: 'https://app.cal.com' });

    const removeCalModal = () => {
      document.querySelectorAll('iframe[src*="cal.com"]').forEach(iframe => {
        let node: Element | null = iframe;
        while (node?.parentElement && node.parentElement !== document.body) {
          node = node.parentElement;
        }
        node?.remove();
      });
      // Also catch any fixed overlay at body level that may not contain an iframe yet
      document.querySelectorAll('body > div').forEach(div => {
        if ((div as HTMLElement).style.position === 'fixed') div.remove();
      });
      document.body.style.overflow = '';
      document.body.style.removeProperty('overflow');
    };

    const handleCalEvent = (e: any) => {
      // Cal.com uses 'booking' for new bookings and 'newBooking' for reschedules
      const start =
        e?.detail?.data?.booking?.startTime    ||
        e?.detail?.data?.newBooking?.startTime ||
        e?.detail?.data?.startTime             ||
        e?.data?.booking?.startTime            ||
        e?.data?.newBooking?.startTime         ||
        e?.data?.startTime                     ||
        e?.detail?.startTime                   ||
        e?.startTime                           || '';

      const calUid =
        e?.detail?.data?.booking?.uid    ||
        e?.detail?.data?.newBooking?.uid ||
        e?.detail?.data?.uid             ||
        e?.data?.booking?.uid            ||
        e?.data?.newBooking?.uid         ||
        e?.data?.uid                     ||
        e?.detail?.uid                   ||
        e?.uid                           || '';

      if (start) setAppointmentTime(start);
      if (calUid) setCalBookingUid(calUid);

      fetch(`${API_BASE}/submit-details`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token: tokenRef.current, appointment_time: start, cal_booking_uid: calUid }),
      }).catch(() => {});

      // Remove modal immediately then retry after a short delay
      removeCalModal();
      setTimeout(removeCalModal, 400);

      setTimeRemaining(HOLD_MINUTES * 60);
      setStep(3);
    };

    win.Cal('on', { action: 'bookingSuccessful', callback: handleCalEvent });
  }, []);

  // ── Load booking state from DB (when token in URL) ────────────────────────
  useEffect(() => {
    if (!urlToken) return;

    fetch(`${API_BASE}/booking?token=${urlToken}`)
      .then(r => {
        if (r.status === 410) { setPageState('expired'); return null; }
        if (!r.ok)            { setPageState('invalid'); return null; }
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setCustomerName(data.customer_name);
        setCustomerEmail(data.customer_email || '');
        setCustomerDob(data.dob || '');
        setCustomerTob(data.time_of_birth || '');
        setCustomerPob(data.place_of_birth || '');
        setCalBookingUid(data.cal_booking_uid || '');

        if (data.status === 'cancelled') {
          setPageState('cancelled');
          return;
        }

        if (data.status === 'refunded') {
          setAppointmentTime(data.appointment_time || '');
          setServiceSelected(data.service_selected || '');
          setPageState('refunded');
          return;
        }

        if (data.status === 'payment_received') {
          setAppointmentTime(data.appointment_time || '');
          setServiceSelected(data.service_selected || '');
          setPageState('confirmed');
          return;
        }

        if (data.details_submitted) {
          setAppointmentTime(data.appointment_time || '');
          setServiceSelected(data.service_selected || '');
          setStripeUrl(data.stripe_url || '');
          setStep(data.appointment_time ? 3 : 2);
          setPageState('ready');
          return;
        }

        // Old-style token — pre-fill name, show intake form
        setName(data.customer_name || '');
        setPageState('intake');
        setStep(1);
      })
      .catch(() => setPageState('invalid'));
  }, [urlToken]);

  // ── Countdown timer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (timeRemaining === null) return;
    if (timeRemaining <= 0) { setSlotExpired(true); return; }
    const t = setTimeout(() => setTimeRemaining(r => r !== null ? r - 1 : null), 1000);
    return () => clearTimeout(t);
  }, [timeRemaining]);

  // ── Warn before leaving on step 3 ─────────────────────────────────────────
  useEffect(() => {
    if (step !== 3 || slotExpired) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [step, slotExpired]);

  const fmtCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // ── Step 1 submit — create self-service booking ───────────────────────────
  const handleIntakeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service to continue.');
      return;
    }

    setSubmitting(true);

    try {
      const resp = await fetch(`${API_BASE}/submit-details`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action:            'create_booking',
          name,
          email,
          phone,
          dob,
          time_of_birth:     timeOfBirth,
          place_of_birth:    placeOfBirth,
          service,
          questions,
          terms_accepted_at: new Date().toISOString(),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      setToken(data.token);
      setServiceSelected(service);
      setStripeUrl(data.stripe_url);
      setCustomerName(name);
      setCustomerEmail(email);
      setCustomerDob(dob);
      setCustomerTob(timeOfBirth);
      setCustomerPob(placeOfBirth);
      setStep(2);
      setPageState('ready');
      setSubmitting(false);
    } catch {
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  // ── Edit appointment ──────────────────────────────────────────────────────
  const handleEditAppointment = async () => {
    setResetting(true);
    try {
      await fetch(`${API_BASE}/submit-details`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, action: 'reset_appointment' }),
      });
    } catch {}
    setPrevAppointmentTime(appointmentTime);
    setAppointmentTime('');
    setCalBookingUid('');
    setResetting(false);
    setStep(2);
  };

  // ── Details modal ─────────────────────────────────────────────────────────
  const openModal = () => {
    setModalName(customerName);
    setModalDob(customerDob);
    setModalTob(customerTob);
    setModalPob(customerPob);
    setModalService(serviceSelected);
    setModalError('');
    setShowModal(true);
  };

  const handleUpdateDetails = async () => {
    if (!modalName || !modalDob || !modalPob || !modalService) {
      setModalError('Name, date of birth, place of birth, and service are required.');
      return;
    }
    setModalError('');
    setUpdating(true);
    try {
      const resp = await fetch(`${API_BASE}/submit-details`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          token,
          action:         'update_details',
          name:           modalName,
          dob:            modalDob,
          time_of_birth:  modalTob,
          place_of_birth: modalPob,
          service:        modalService,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setModalError(data.error || 'Could not update. Please try again.');
        setUpdating(false);
        return;
      }
      setCustomerName(data.name);
      setCustomerDob(data.dob);
      setCustomerTob(data.time_of_birth);
      setCustomerPob(data.place_of_birth);
      setServiceSelected(data.service);
      setStripeUrl(data.stripe_url);
      setShowModal(false);
    } catch {
      setModalError('Network error. Please try again.');
    }
    setUpdating(false);
  };

  // ── Static page states ────────────────────────────────────────────────────

  if (pageState === 'loading') return (
    <div className="sd-page">
      <div className="sd-card sd-center"><p className="sd-muted">Loading...</p></div>
    </div>
  );

  if (pageState === 'invalid') return (
    <div className="sd-page">
      <div className="sd-card sd-center">
        <div className="sd-big-icon">✨</div>
        <h1>Link Invalid</h1>
        <p className="sd-muted">
          This link is invalid or has expired.
          Please <a href="mailto:astrologerkavitha@gmail.com">contact us</a> if you need help.
        </p>
      </div>
    </div>
  );

  if (pageState === 'expired') return (
    <div className="sd-page">
      <div className="sd-card sd-center">
        <div className="sd-big-icon">🌙</div>
        <h1>Link Expired</h1>
        <p className="sd-muted">
          This link has expired or your appointment date has already passed.
        </p>
        <p className="sd-muted">
          Please <a href="mailto:astrologerkavitha@gmail.com">contact us</a> if you need help.
        </p>
      </div>
    </div>
  );

  if (pageState === 'cancelled') return (
    <div className="sd-page">
      <div className="sd-card sd-center">
        <div className="sd-big-icon">🌙</div>
        <h1>Booking Session Expired</h1>
        <p className="sd-muted">
          Your booking session has expired. Your appointment slot was released after 15 minutes.
          Please start a new booking to pick a new time.
        </p>
        <a href="/" className="sd-primary-btn" style={{ display: 'inline-block', marginTop: '1.5rem' }}>
          Start New Booking
        </a>
      </div>
    </div>
  );

  if (pageState === 'refunded') return (
    <div className="sd-page">
      <div className="sd-card sd-center">
        <div className="sd-big-icon">💫</div>
        <h1>Booking Refunded</h1>
        <p className="sd-muted">
          This booking{serviceSelected ? ` (${serviceSelected})` : ''} has been cancelled and your payment has
          been refunded. There is nothing further needed from you, and no payment is due.
        </p>
        {appointmentTime && (
          <div className="sd-appointment-badge" style={{ textDecoration: 'line-through', opacity: 0.6 }}>
            📅 {fmtDate(appointmentTime)}
          </div>
        )}
        <p className="sd-muted">
          If you would like to book a new session, you are welcome to start fresh at any time.
        </p>
        <a href="/" className="sd-primary-btn" style={{ display: 'inline-block', marginTop: '1.5rem' }}>
          Start New Booking
        </a>
      </div>
    </div>
  );

  if (pageState === 'confirmed') return (
    <div className="sd-page">
      <div className="sd-card">
        <div className="sd-header">
          <div className="sd-big-icon">🌟</div>
          <h1>Booking Confirmed!</h1>
          <p className="sd-muted">Thank you, <strong>{customerName}</strong>. Your reading is confirmed.</p>
        </div>

        {appointmentTime && (
          <div className="sd-appointment-badge">
            📅 <strong>{fmtDate(appointmentTime)}</strong>
          </div>
        )}
        {serviceSelected && (
          <div className="sd-appointment-badge">
            🔮 <strong>{serviceSelected}</strong>
            {' — '}{SERVICES.find(s => s.name === serviceSelected)?.price}
          </div>
        )}

        <div className="sd-confirmed-info">
          <p>Here's what happens next:</p>
          <ul>
            <li>📄 Astrologer Kavitha will prepare your birth chart. You will receive a <strong>secure download link by email before your appointment</strong>.</li>
            <li>📧 You will receive a <strong>calendar invite</strong> with the online meeting link by email.</li>
            <li>💳 A separate <strong>payment confirmation</strong> email is on its way.</li>
          </ul>
          <p className="sd-muted">
            Questions? Reply to your confirmation email or WhatsApp{' '}
            <strong>+1 425-219-0869</strong>.
          </p>
        </div>
      </div>
    </div>
  );

  // ── Wizard ────────────────────────────────────────────────────────────────

  const STEP_LABELS = ['Your Details', 'Book Appointment', 'Payment'];

  return (
    <div className="sd-page">
      <Helmet>
        <title>Book a Vedic Astrology Reading | Cosmic Kavi</title>
        <meta name="description" content="Book your personalised Vedic astrology reading with Kavitha Nandy. Choose your service, pick a time, and pay securely online. Remote consultations across the USA from $25." />
        <link rel="canonical" href="https://cosmickavi.com/submit-details" />
      </Helmet>

      {/* Details edit modal */}
      {showModal && (
        <div className="sd-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="sd-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Your Details</h3>
            <p className="sd-muted" style={{ marginBottom: '1.25rem' }}>
              Update your information. Your appointment time will remain the same.
            </p>

            <div className="sd-form">
              <div className="sd-field">
                <label htmlFor="m-name">Full Name</label>
                <input id="m-name" type="text"
                  value={modalName} onChange={e => setModalName(e.target.value)} required />
              </div>

              <div className="sd-field">
                <label htmlFor="m-dob">Date of Birth</label>
                <input id="m-dob" type="date"
                  value={modalDob} onChange={e => setModalDob(e.target.value)} required />
              </div>

              <div className="sd-field">
                <label htmlFor="m-tob">
                  Time of Birth <span className="sd-hint">(optional)</span>
                </label>
                <input id="m-tob" type="time"
                  value={modalTob} onChange={e => setModalTob(e.target.value)} />
              </div>

              <div className="sd-field">
                <label htmlFor="m-pob">
                  Place of Birth <span className="sd-hint">(City, Country)</span>
                </label>
                <input id="m-pob" type="text"
                  value={modalPob} onChange={e => setModalPob(e.target.value)} required />
              </div>

              <div className="sd-field">
                <label htmlFor="m-svc">Service</label>
                <select id="m-svc" value={modalService} onChange={e => setModalService(e.target.value)} required>
                  <option value="">Choose a service...</option>
                  {SERVICES.map(s => (
                    <option key={s.name} value={s.name}>{s.name} — {s.price}</option>
                  ))}
                </select>
              </div>

              {modalService && (
                <div className="sd-price-preview">
                  <span>Total due today:</span>
                  <strong>{SERVICES.find(s => s.name === modalService)?.price}</strong>
                </div>
              )}

              {modalError && <div className="sd-error">{modalError}</div>}

              <div className="sd-modal-actions">
                <button className="sd-secondary-btn" onClick={() => setShowModal(false)} disabled={updating}>
                  Cancel
                </button>
                <button className="sd-primary-btn" onClick={handleUpdateDetails} disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sd-card">
        <div className="sd-header">
          <div className="sd-big-icon">🌙</div>
          <h1>{step === 1 ? 'Book a Reading' : `Welcome, ${customerName}`}</h1>
          <p className="sd-muted">
            {step === 1
              ? 'Fill in your details to begin your Vedic astrology journey with Kavitha Nandy.'
              : 'Complete the steps below to confirm your reading.'}
          </p>
        </div>

        {/* Progress bar */}
        <div className="sd-progress">
          {STEP_LABELS.map((label, i) => {
            const num    = i + 1;
            const active = step === num;
            const done   = step > num;
            return (
              <React.Fragment key={num}>
                <div className={`sd-step ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
                  <div className="sd-step-circle">{done ? '✓' : num}</div>
                  <span className="sd-step-label">{label}</span>
                </div>
                {i < STEP_LABELS.length - 1 && <div className={`sd-connector ${done ? 'done' : ''}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Step 1: Intake form ── */}
        {step === 1 && (
          <div className="sd-step-content">
            <h2>Step 1 — Your Details</h2>
            <p>Please provide your information and birth details so Kavitha can prepare your personalised reading.</p>
            <form onSubmit={handleIntakeSubmit} className="sd-form">

              <div className="sd-field">
                <label htmlFor="name">Full Name</label>
                <input id="name" type="text" placeholder="Your full name"
                  value={name} onChange={e => setName(e.target.value)} required />
              </div>

              <div className="sd-field">
                <label htmlFor="email">Email Address</label>
                <input id="email" type="email" placeholder="you@example.com"
                  value={email} onChange={e => setEmail(e.target.value)} required />
              </div>

              <div className="sd-field">
                <label htmlFor="phone">
                  Phone / WhatsApp <span className="sd-hint">(optional)</span>
                </label>
                <input id="phone" type="tel" placeholder="+1 425-555-0100"
                  value={phone} onChange={e => setPhone(e.target.value)} />
              </div>

              <div className="sd-field">
                <label htmlFor="dob">Date of Birth</label>
                <input id="dob" type="date"
                  value={dob} onChange={e => setDob(e.target.value)} required />
              </div>

              <div className="sd-field">
                <label htmlFor="tob">
                  Time of Birth <span className="sd-hint">(optional — include if known)</span>
                </label>
                <input id="tob" type="time"
                  value={timeOfBirth} onChange={e => setTimeOfBirth(e.target.value)} />
              </div>

              <div className="sd-field">
                <label htmlFor="place">
                  Place of Birth <span className="sd-hint">(City, Country)</span>
                </label>
                <input id="place" type="text" placeholder="e.g. Mumbai, India"
                  value={placeOfBirth} onChange={e => setPlaceOfBirth(e.target.value)} required />
              </div>

              <div className="sd-field">
                <label htmlFor="service">Select Service</label>
                <select id="service" value={service} onChange={e => setService(e.target.value)} required>
                  <option value="">Choose a service...</option>
                  {SERVICES.map(s => (
                    <option key={s.name} value={s.name}>{s.name} — {s.price}</option>
                  ))}
                </select>
              </div>

              {service && (
                <div className="sd-price-preview">
                  <span>Total due today:</span>
                  <strong>{SERVICES.find(s => s.name === service)?.price}</strong>
                </div>
              )}

              <div className="sd-field">
                <label htmlFor="questions">
                  Questions or Focus Areas <span className="sd-hint">(optional)</span>
                </label>
                <textarea
                  id="questions"
                  placeholder="What areas of life would you like to explore? (career, relationships, health...)"
                  value={questions}
                  onChange={e => setQuestions(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="sd-checkbox-field">
                <label htmlFor="agree-terms">
                  <input
                    id="agree-terms"
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={e => setAgreedToTerms(e.target.checked)}
                    required
                  />
                  <span>
                    I have read and agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  </span>
                </label>
              </div>

              {error && <div className="sd-error">{error}</div>}

              <button type="submit" className="sd-primary-btn" disabled={submitting || !agreedToTerms}>
                {submitting ? 'Saving...' : 'Continue to Appointment →'}
              </button>
              <p className="sd-secure-note">🔒 Your information is kept private and secure</p>
            </form>
          </div>
        )}

        {/* ── Step 2: Cal.com appointment booking ── */}
        {step === 2 && (
          <div className="sd-step-content">
            <h2>Step 2 — Book Your Appointment</h2>
            <p>Choose a date and time for your reading. You'll move to payment automatically once your slot is confirmed.</p>

            {/* Details summary card — same as Step 3 */}
            <div className="sd-summary-card">
              <div className="sd-summary-header">
                <span className="sd-summary-title">Your Details</span>
                <button className="sd-edit-btn" onClick={openModal}>Edit</button>
              </div>
              <div className="sd-summary-row">
                <span className="sd-summary-label">👤 Name</span>
                <span>{customerName}</span>
              </div>
              <div className="sd-summary-row">
                <span className="sd-summary-label">🎂 Date of Birth</span>
                <span>{fmtDob(customerDob) || '—'}</span>
              </div>
              {customerTob && (
                <div className="sd-summary-row">
                  <span className="sd-summary-label">🕐 Time of Birth</span>
                  <span>{fmtTime(customerTob)}</span>
                </div>
              )}
              <div className="sd-summary-row">
                <span className="sd-summary-label">📍 Place of Birth</span>
                <span>{customerPob || '—'}</span>
              </div>
              <div className="sd-summary-row sd-summary-service">
                <span className="sd-summary-label">🔮 Service</span>
                <span>
                  <strong>{serviceSelected}</strong>
                  {' — '}
                  <strong className="sd-summary-price">{SERVICES.find(s => s.name === serviceSelected)?.price}</strong>
                </span>
              </div>
            </div>

            <button
              data-cal-link={`${CAL_NAMESPACE}?duration=${SERVICE_DURATIONS[serviceSelected] || 60}`}
              data-cal-config='{"layout":"month_view"}'
              className="sd-primary-btn"
            >
              📅 Book Appointment
            </button>
          </div>
        )}

        {/* ── Step 3: Payment ── */}
        {step === 3 && (
          <div className="sd-step-content">
            {slotExpired ? (
              <div className="sd-expired">
                <div className="sd-expired-icon">🕐</div>
                <h2>Your slot was released</h2>
                <p className="sd-muted">
                  The {HOLD_MINUTES}-minute hold on your appointment has expired.
                  Please start a new booking to choose a different time.
                </p>
                <a href="/submit-details" className="sd-primary-btn" style={{ display: 'inline-block', marginTop: '1.5rem' }}>
                  Start New Booking
                </a>
              </div>
            ) : (
            <>
            <h2>Step 3 — Complete Your Payment</h2>
            <p>Your appointment is reserved. Review your details below, then click to pay securely.</p>

            {timeRemaining !== null && (
              <div className={`sd-countdown ${timeRemaining < 180 ? 'sd-countdown-urgent' : ''}`}>
                ⏰ Slot held for <strong>{fmtCountdown(timeRemaining)}</strong> — complete payment now to confirm
              </div>
            )}

            {/* Appointment badge */}
            {appointmentTime && (
              <>
                <div className="sd-appointment-badge sd-badge-row">
                  <span>
                    {prevAppointmentTime && (
                      <span className="sd-appt-prev">📅 {fmtDate(prevAppointmentTime)}</span>
                    )}
                    <span className={prevAppointmentTime ? 'sd-appt-new' : ''}>
                      📅 <strong>{fmtDate(appointmentTime)}</strong>
                    </span>
                  </span>
                  <button className="sd-edit-btn" onClick={handleEditAppointment} disabled={resetting}>
                    {resetting ? '...' : 'Edit'}
                  </button>
                </div>
                {prevAppointmentTime && (
                  <p className="sd-reschedule-note">
                    You may receive a cancellation email for the previous time — you can ignore it.
                  </p>
                )}
              </>
            )}

            {/* Customer details summary card */}
            <div className="sd-summary-card">
              <div className="sd-summary-header">
                <span className="sd-summary-title">Your Details</span>
                <button className="sd-edit-btn" onClick={openModal}>Edit</button>
              </div>
              <div className="sd-summary-row">
                <span className="sd-summary-label">👤 Name</span>
                <span>{customerName}</span>
              </div>
              <div className="sd-summary-row">
                <span className="sd-summary-label">🎂 Date of Birth</span>
                <span>{fmtDob(customerDob) || '—'}</span>
              </div>
              {customerTob && (
                <div className="sd-summary-row">
                  <span className="sd-summary-label">🕐 Time of Birth</span>
                  <span>{fmtTime(customerTob)}</span>
                </div>
              )}
              <div className="sd-summary-row">
                <span className="sd-summary-label">📍 Place of Birth</span>
                <span>{customerPob || '—'}</span>
              </div>
              <div className="sd-summary-row sd-summary-service">
                <span className="sd-summary-label">🔮 Service</span>
                <span>
                  <strong>{serviceSelected}</strong>
                  {' — '}
                  <strong className="sd-summary-price">{SERVICES.find(s => s.name === serviceSelected)?.price}</strong>
                </span>
              </div>
            </div>

            <a href={stripeUrl} className="sd-primary-btn">💳 Complete Payment →</a>
            <p className="sd-secure-note">🔒 Payments are processed securely by Stripe</p>
            </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default SubmitDetails;
